import Highcharts from 'highcharts';

const installedCharts = new WeakSet<Highcharts.Chart>();
const chartHighlights = new WeakMap<Highcharts.Chart, {
  clear: () => void;
  show: (series: Highcharts.Series) => void;
}>();
let globalHighlightInstalled = false;
const hiddenSeriesLayers = new WeakSet<Highcharts.Chart>();

function syncSeriesLayerVisibility(chart: Highcharts.Chart): void {
  const seriesGroup = (chart as Highcharts.Chart & { seriesGroup?: Highcharts.SVGElement }).seriesGroup;
  if (chart.series.some(series => series.visible)) {
    if (hiddenSeriesLayers.has(chart) && seriesGroup) {
      seriesGroup.show();
      hiddenSeriesLayers.delete(chart);
    }
  } else {
    if (seriesGroup) {
      seriesGroup.hide();
      hiddenSeriesLayers.add(chart);
    }
    chartHighlights.get(chart)?.clear();
    chart.tooltip?.hide(0);
  }
}

/** One reusable overlay per chart; the Boost image and source data are untouched. */
export function installBoostLineHighlight(chart: Highcharts.Chart): void {
  if (installedCharts.has(chart) || !chart.series.some(series => series.type === 'line')) return;
  installedCharts.add(chart);

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:absolute;pointer-events:none;z-index:3;';
  chart.container.appendChild(canvas);
  const marker = document.createElement('span');
  marker.style.cssText = [
    'position:absolute',
    'display:none',
    'width:6px',
    'height:6px',
    'border-radius:50%',
    'pointer-events:none',
    'transform:translate(-50%,-50%)',
    'z-index:4',
  ].join(';');
  chart.container.appendChild(marker);
  const context = canvas.getContext('2d');
  let active: Highcharts.Series | undefined;
  let frame = 0;
  let pointerFrame = 0;
  let renderVersion = 0;
  let restoreSeriesStyle: (() => void) | undefined;
  const unbind: ReturnType<typeof Highcharts.addEvent>[] = [];
  const resolvedColors = new Map<string, string>();

  const resolveColor = (color: Highcharts.ColorType | undefined): string => {
    if (typeof color !== 'string') return '#555';
    const cached = resolvedColors.get(color);
    if (cached) return cached;

    const probe = document.createElement('span');
    probe.style.cssText = `position:absolute;visibility:hidden;color:${color}`;
    chart.container.appendChild(probe);
    const resolved = getComputedStyle(probe).color || '#555';
    probe.remove();
    resolvedColors.set(color, resolved);
    return resolved;
  };

  const clear = () => {
    renderVersion++;
    cancelAnimationFrame(frame);
    cancelAnimationFrame(pointerFrame);
    pointerFrame = 0;
    frame = 0;
    active = undefined;
    restoreSeriesStyle?.();
    restoreSeriesStyle = undefined;
    context?.clearRect(0, 0, canvas.width, canvas.height);
    canvas.style.display = 'none';
    marker.style.display = 'none';
  };
  const showMarker = (point: Highcharts.Point) => {
    const color = point.color ?? point.series.color;
    marker.style.left = `${chart.plotLeft + point.plotX}px`;
    marker.style.top = `${chart.plotTop + point.plotY!}px`;
    marker.style.backgroundColor = resolveColor(color);
    marker.style.display = 'block';
  };
  const show = (series: Highcharts.Series) => {
    if (active === series || !series.visible || series.type !== 'line' || !context) return;
    clear();
    active = series;
    const currentRenderVersion = renderVersion;
    frame = requestAnimationFrame(() => {
      frame = 0;
      // The series may have been hidden or the chart redrawn after this frame
      // was queued. Never paint a stale highlight back onto the overlay.
      if (currentRenderVersion !== renderVersion || !series.visible) return;
      canvas.style.display = 'block';
      const ratio = window.devicePixelRatio || 1;
      const width = chart.plotWidth;
      const height = chart.plotHeight;
      canvas.style.left = `${chart.plotLeft}px`;
      canvas.style.top = `${chart.plotTop}px`;
      canvas.width = Math.ceil(width * ratio);
      canvas.height = Math.ceil(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.strokeStyle = resolveColor(series.color);
      context.lineWidth = ((series.options as Highcharts.SeriesLineOptions).lineWidth ?? 1) + 1;
      context.lineJoin = 'round';
      context.beginPath();
      let connected = false;
      const boostedSeries = series as Highcharts.Series & { getColumn?: (key: string) => unknown[] };
      const xValues = boostedSeries.getColumn?.('x') ?? [];
      const yValues = boostedSeries.getColumn?.('y') ?? [];
      const pointCount = Math.min(xValues.length, yValues.length);
      for (let index = 0; index < pointCount; index++) {
        const xValue = xValues[index];
        const yValue = yValues[index];
        if (typeof xValue !== 'number' || typeof yValue !== 'number') {
          connected = false;
          continue;
        }
        const x = series.xAxis.toPixels(xValue) - chart.plotLeft;
        const y = series.yAxis.toPixels(yValue) - chart.plotTop;
        if (connected) context.lineTo(x, y);
        else context.moveTo(x, y);
        connected = true;
      }
      context.stroke();
      // Desaturate the rendered series only; axes, labels and the colored overlay stay intact.
      const seriesLayer = chart.container.querySelector<SVGElement>('.highcharts-series-group');
      if (seriesLayer) {
        const filter = seriesLayer.style.filter;
        const opacity = seriesLayer.style.opacity;
        seriesLayer.style.filter = 'grayscale(1)';
        seriesLayer.style.opacity = '0.3';
        restoreSeriesStyle = () => {
          seriesLayer.style.filter = filter;
          seriesLayer.style.opacity = opacity;
        };
      }
    });
  };

  chartHighlights.set(chart, { clear, show });

  for (const series of chart.series) {
    unbind.push(Highcharts.addEvent(series, 'mouseOver', () => show(series)));
    unbind.push(Highcharts.addEvent(series, 'mouseOut', clear));
    unbind.push(Highcharts.addEvent(series, 'hide', clear));
    const legend = (series.legendItem as { group?: Highcharts.SVGElement } | undefined)?.group?.element;
    if (legend) {
      unbind.push(Highcharts.addEvent(legend, 'mouseenter', () => show(series)));
      unbind.push(Highcharts.addEvent(legend, 'mouseleave', clear));
    }
  }
  unbind.push(Highcharts.addEvent(chart.container, 'mouseleave', clear));
  unbind.push(Highcharts.addEvent(chart.container, 'mousemove', (event: MouseEvent) => {
    cancelAnimationFrame(pointerFrame);
    const position = chart.pointer.normalize(event);
    const currentRenderVersion = renderVersion;
    pointerFrame = requestAnimationFrame(() => {
      pointerFrame = 0;
      if (currentRenderVersion !== renderVersion) return;
      if (!chart.isInsidePlot(position.chartX - chart.plotLeft, position.chartY - chart.plotTop)) return;
      const series = chart.hoverPoint?.series;
      if (series && chart.hoverPoint) {
        show(series);
        showMarker(chart.hoverPoint);
      }
      else clear();
    });
  }));
  unbind.push(Highcharts.addEvent(chart.container, 'mousedown', clear));
  unbind.push(Highcharts.addEvent(chart, 'redraw', clear));
  Highcharts.addEvent(chart, 'destroy', () => {
    clear();
    cancelAnimationFrame(pointerFrame);
    unbind.forEach(remove => remove());
    canvas.remove();
    marker.remove();
    installedCharts.delete(chart);
    chartHighlights.delete(chart);
  });
}

export function setBoostLineHighlight(chart: Highcharts.Chart, seriesName?: string): void {
  installBoostLineHighlight(chart);
  const highlight = chartHighlights.get(chart);
  if (!seriesName) {
    highlight?.clear();
    return;
  }

  const series = chart.series.find(item => item.name === seriesName);
  if (series) highlight?.show(series);
}

export function installGlobalBoostLineHighlight(): void {
  if (globalHighlightInstalled) return;
  globalHighlightInstalled = true;

  Highcharts.setOptions({ tooltip: { outside: true } });
  Highcharts.addEvent(Highcharts.Chart, 'load', function () {
    installBoostLineHighlight(this);
  });
  // Class events cover every chart, including custom legend handlers and
  // series added after chart creation. Do not toggle visibility a second time.
  Highcharts.addEvent(Highcharts.Chart, 'render', function () {
    syncSeriesLayerVisibility(this);
  });
  for (const event of ['show', 'hide']) {
    Highcharts.addEvent(Highcharts.Series, event, function () {
      chartHighlights.get(this.chart)?.clear();
      syncSeriesLayerVisibility(this.chart);
    });
  }
}
